import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import FinalCta from "../sections/FinalCta";
import NotFoundPage from "./NotFoundPage";
import { JOURNAL, JOURNAL_IMAGE_ALTS, resolveImage } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JournalArticlePage() {
  const { slug } = useParams();
  const [note, setNote] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/journal/${slug}`)
      .then((res) => setNote(res.data))
      .catch(() => {
        const fallback = JOURNAL.find((j) => j.slug === slug);
        if (fallback) setNote(fallback);
        else setMissing(true);
      });
  }, [slug]);

  if (missing) {
    return <NotFoundPage testid="journal-article-missing" />;
  }

  if (!note) {
    return (
      <>
        <Seo title="The Jamoon Journal" description="Loading a field note from The Jamoon." path={`/journal/${slug}`} noindex />
        <main className="min-h-[60vh]" data-testid="journal-article-loading" />
      </>
    );
  }

  const paragraphs = (note.body || note.text || "").split(/\n\s*\n/).filter(Boolean);

  return (
    <main data-testid="journal-article-page">
      <Seo
        title={`${note.title} | The Jamoon Journal`}
        description={note.excerpt || note.text}
        path={`/journal/${slug}`}
        image={resolveImage(note.image, 1200, 630)}
        imageAlt={note.imageAlt || JOURNAL_IMAGE_ALTS[note.slug] || `Photograph accompanying ${note.title}, a story from The Jamoon`}
        type="article"
        datePublished={note.created_at}
        dateModified={note.updated_at || note.created_at}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Journal", path: "/journal" },
          { name: note.title, path: `/journal/${slug}` },
        ]}
      />
      <article className="mx-auto max-w-3xl px-6 pb-24 pt-36 sm:pt-44">
        <Reveal>
          <Link to="/journal" data-testid="article-back-link" className="text-[11px] font-medium tracking-[0.25em] text-ivory/50 transition-colors duration-300 hover:text-brass">
            ← BACK TO THE JOURNAL
          </Link>
        </Reveal>
        <Reveal delay={0.05}>
          <p className="mt-10 text-[11px] font-medium tracking-[0.3em] text-brass">
            {(note.tag || "FIELD NOTES").toUpperCase()}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight text-ivory sm:text-5xl">
            {note.title}
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 font-serif text-xl italic leading-relaxed text-ivory/70">
            {note.excerpt || note.text}
          </p>
        </Reveal>
        {note.image && (
          <Reveal delay={0.2}>
            <img
              src={resolveImage(note.image, 1400, 900)}
              alt={note.imageAlt || JOURNAL_IMAGE_ALTS[note.slug] || `Photograph accompanying ${note.title}`}
              className="mt-10 aspect-[16/10] w-full object-cover"
            />
          </Reveal>
        )}
        <div className="mt-12 space-y-7">
          {paragraphs.map((p, i) => (
            <Reveal key={i} delay={0.05 * Math.min(i, 4)}>
              <p data-testid={`article-para-${i}`} className="text-base font-light leading-loose text-ivory/80 first:font-serif first:text-xl first:leading-relaxed first:text-ivory">
                {p}
              </p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-14 border-t border-ivory/10 pt-8 text-sm italic text-ivory/45">
            Written at The Jamoon, beside the Ramganga.
          </p>
        </Reveal>
      </article>
      <FinalCta />
    </main>
  );
}
